import { createClient } from '@supabase/supabase-js';
import { projectId, publicAnonKey, supabaseUrl } from '../utils/supabase/info';
import { toast } from 'sonner';

export { projectId, publicAnonKey, supabaseUrl };

const supabase = createClient(
  supabaseUrl || `https://${projectId}.supabase.co`,
  publicAnonKey
);

export { supabase };

export function cleanImageUrl(url: any): string {
  if (!url) return '';
  let cleaned = String(url).trim();
  
  if (cleaned.startsWith('data:image')) {
    return cleaned;
  }
  
  // 1. Handle comma-separated metadata often added by WP plugins/themes
  // e.g. logo.png,96,96,false -> logo.png?96,96,false
  // We replace the FIRST comma after the extension with a ?
  if (cleaned.includes(',')) {
    cleaned = cleaned.replace(/(\.(?:webp|png|jpg|jpeg|gif|svg|ico)),/i, '$1?');
  }
  
  // 2. Remove everything after a space (handle srcset-like strings)
  cleaned = cleaned.split(/\s/)[0];

  /**
   * 3. Protocol normalization
   * We skip the aggressive truncation regex from before because it was 
   * stripping the query parameters we just fixed (like ?96,96,false)
   */
  if (cleaned.startsWith('//')) {
    cleaned = 'https:' + cleaned;
  } else if (cleaned && !cleaned.startsWith('http') && cleaned.includes('.')) {
    cleaned = 'https://' + cleaned;
  }
  
  return cleaned;
}
/**
 * Utility for interacting with the WooCommerce REST API.
 */

export interface WooCommerceConfig {
  url: string;
  consumerKey: string;
  consumerSecret: string;
  wpUsername?: string;
  wpAppPassword?: string;
  version: string;
  useProxy?: boolean;
  hiddenTabs?: string[];
  hiddenTabsManager?: string[];
  customUrl?: string;
  logoUrl?: string;
  customDomain?: string;

  appleTouchIconUrl?: string;
  themeColor?: string;
  appName?: string;

  id?: string;
  slug?: string;
  name?: string;
  logo_url?: string;
  brand_color?: string;
  theme_mode?: 'light' | 'dark';

  /** LojaPilot connection model — absent means a legacy WooCommerce API store. */
  connectionType?: 'woocommerce_api' | 'woocommerce_plugin' | 'native';
  nativeStoreId?: string;
  connectionStatus?: string;
  onboardingCompleted?: boolean;

  /** Master can disable the "Sou Cliente" login tab for a store (default: on). */
  clientLoginEnabled?: boolean;

  /** Módulo Agente IA — extra mensal sobre o plano (configurado pelo Master). */
  aiAgentEnabled?: boolean;
  /** Embed code do agente (snippet <script> ou IIFE) — ou apenas o site ID. */
  aiAgentScript?: string;
  /** Bearer token que o agente usa na API /agent/{slug}/* para ler dados da loja. */
  aiAgentApiKey?: string;
}

/**
 * Extrai { src, siteId, apiUrl } de um snippet de embed do agente.
 * Aceita o formato <script src="..." data-site-id="..." data-api-url="...">,
 * o IIFE `s.src = '...'; s.dataset.siteId = '...'`, ou um site ID simples
 * (usa os endpoints hosted por defeito).
 */
export function parseAgentEmbed(raw: string): { src: string; siteId: string; apiUrl: string } | null {
  const t = (raw || '').trim();
  if (!t) return null;
  const pick = (...patterns: RegExp[]) => {
    for (const p of patterns) {
      const m = t.match(p);
      if (m?.[1]) return m[1];
    }
    return '';
  };
  if (/^[a-zA-Z0-9_-]{6,}$/.test(t)) {
    return {
      src: 'https://agent.swiftapps.cloud/widget/chatbot.js',
      siteId: t,
      apiUrl: 'https://agent.swiftapps.cloud',
    };
  }
  const src = pick(/src\s*=\s*["']([^"']+)["']/i, /\.src\s*=\s*['"]([^'"]+)['"]/);
  const siteId = pick(/data-site-id\s*=\s*["']([^"']+)["']/i, /\.siteId\s*=\s*['"]([^'"]+)['"]/, /site_id\s*[:=]\s*['"]([^'"]+)['"]/i);
  const apiUrl = pick(/data-api-url\s*=\s*["']([^"']+)["']/i, /\.apiUrl\s*=\s*['"]([^'"]+)['"]/, /api_url\s*[:=]\s*['"]([^'"]+)['"]/i);
  if (!src) return null;
  return { src, siteId, apiUrl };
}

export const getServerUrl = (endpoint: string) => {
  const isLocal = supabaseUrl.includes('127.0.0.1') || supabaseUrl.includes('localhost');
  if (isLocal && typeof window !== 'undefined') {
    return `/supabase-api/functions/v1/make-server-75caf0c1/${endpoint}`;
  }
  const base = supabaseUrl ? `${supabaseUrl}/functions/v1/make-server-75caf0c1` : `https://${projectId}.supabase.co/functions/v1/make-server-75caf0c1`;
  return `${base}/${endpoint}`;
};

/**
 * Simple request queue to prevent overwhelming the server with parallel requests
 */
const requestQueue: (() => Promise<any>)[] = [];
let activeRequests = 0;
const MAX_CONCURRENT_REQUESTS = 5; // Allow up to 5 concurrent requests for better performance
const MIN_REQUEST_DELAY = 100; // 100ms between requests to prevent overwhelming the server
let lastRequestTime = 0;

async function processQueue() {
  if (activeRequests >= MAX_CONCURRENT_REQUESTS || requestQueue.length === 0) return;
  
  const now = Date.now();
  const timeSinceLast = now - lastRequestTime;
  if (timeSinceLast < MIN_REQUEST_DELAY) {
    setTimeout(processQueue, MIN_REQUEST_DELAY - timeSinceLast);
    return;
  }

  activeRequests++;
  const task = requestQueue.shift();
  if (task) {
    lastRequestTime = Date.now();
    try {
      await task();
    } catch (e) {
      console.error("[WooRequest] Queue task failed", e);
    } finally {
      activeRequests--;
      setTimeout(processQueue, 10); // Small buffer before processing the next item
      processQueue(); // Keep processing immediately if under limit
    }
  }
}

/**
 * WooCommerce API request helper.
 */
export async function wooRequest(config: WooCommerceConfig, endpoint: string, options: Omit<RequestInit, 'body'> & { body?: any } = {}, basePath: string = 'wc/v3'): Promise<any> {
  // LojaPilot native stores are served by the Supabase backend, not a remote
  // WooCommerce site — delegate before any URL/credential validation.
  if (config?.connectionType === 'native') {
    const { nativeRequest } = await import('./nativeStore');
    return nativeRequest(config, endpoint, options, basePath);
  }

  // Identify if this is a background/notification request
  const isBackground = endpoint.includes('orders') && !options.body && (options as any).isNotification;

  return new Promise((resolve, reject) => {
    // If the queue is too full and this is just a background check, skip it
    if (isBackground && requestQueue.length > 2) {
      console.log("[WooRequest] Skipping background request due to busy queue");
      resolve([]); 
      return;
    }

    const execute = async () => {
      let proxyUrl: string;
      let fetchOptions: RequestInit;

      try {
        // 1. Extreme cleaning of keys and URL
        let inputUrl = config.url?.trim() || "";
        
        // Safety check: Avoid requests to empty or placeholder URLs
        const isInvalidUrlInput = !inputUrl || 
                                   inputUrl === 'https://' || 
                                   inputUrl === 'http://' || 
                                   inputUrl.toLowerCase().includes('wp-json') ||
                                   inputUrl.length < 5;

        // Only wc/* admin endpoints require consumer keys — swiftwoo/v1 (JWT
        // customer auth) and wp/v2 (WP user creds) must not be blocked by a
        // missing CK/CS, or plugin-store client login fails before reaching
        // the server.
        const needsWooKeys = basePath.startsWith('wc/');
        if (isInvalidUrlInput || (needsWooKeys && (!config.consumerKey || !config.consumerSecret))) {
          const err = new Error("MISSING_CONFIG: Store URL or API keys are not configured correctly. The URL should be your site homepage (e.g., https://mystore.com).");
          (err as any).isConfigError = true;
          throw err;
        }

        // Ensure protocol
        if (!inputUrl.startsWith('http')) inputUrl = `https://${inputUrl}`;
        
        let baseUrl: string;
        try {
          const urlObj = new URL(inputUrl);
          baseUrl = urlObj.origin; // This gets "https://mystore.com"
        } catch (e) {
          throw new Error("INVALID_URL: The Store URL provided is invalid. Format: https://mystore.com");
        }

        // Double check baseUrl
        if (baseUrl.includes('wp-json') || baseUrl.length < 10) {
          throw new Error("INVALID_CONFIG: Store URL is invalid or pointing to wp-json.");
        }
        
        const ck = config.consumerKey.trim();
        const cs = config.consumerSecret.trim();

        // 2. Validate key prefixes
        if (ck && !ck.startsWith('ck_')) {
           throw new Error("INVALID_KEY: Consumer Key must start with 'ck_'.");
        }
        if (cs && !cs.startsWith('cs_')) {
           throw new Error("INVALID_SECRET: Consumer Secret must start with 'cs_'.");
        }
        
        // 3. Construct Final URL
        let finalTargetUrl: string;
        try {
          const rawEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
          const [pathPart, queryPart] = rawEndpoint.split('?');
          const cleanEndpoint = pathPart || '';

          const apiPath = `wp-json/${basePath}/${cleanEndpoint}`;
          const fullPath = `${baseUrl}/${apiPath}`;
          const urlObj = new URL(fullPath);

          const extraQuery = new URLSearchParams(queryPart || '');
          for (const [key, value] of extraQuery.entries()) {
            urlObj.searchParams.append(key, value);
          }

          const isWooApiEndpoint = ['wc/v1', 'wc/v2', 'wc/v3'].includes(basePath);
          if (isWooApiEndpoint) {
            urlObj.searchParams.set('consumer_key', ck);
            urlObj.searchParams.set('consumer_secret', cs);
          }
          urlObj.searchParams.set('_t', Date.now().toString());

          finalTargetUrl = urlObj.toString();
        } catch (e) {
          throw new Error("INVALID_URL: Failed to construct API URL.");
        }
        
        const authHeaderValue = `Basic ${btoa(`${ck}:${cs}`)}`;

        const { data: { session } } = await supabase.auth.getSession();

        // Proxy setup
        proxyUrl = getServerUrl('proxy');
        const headers: Record<string, string> = {
          ...((options.headers as Record<string, string>) || {}),
          'X-Proxy-Target': finalTargetUrl,
          'apikey': publicAnonKey,
          'Authorization': session?.access_token ? "Bearer " + session.access_token : publicAnonKey,
        };

        const explicitAuthHeader = (options.headers as Record<string, string> | undefined)?.['X-WC-Authorization'] ||
          (options.headers as Record<string, string> | undefined)?.['x-wc-authorization'];

        const isJwtAuthRoute = basePath === 'swiftwoo/v1';
        const isWordPressMediaRoute = basePath === 'wp/v2';
        const skipBasicAuth = isWordPressMediaRoute && endpoint.includes('/media') && (!options.method || options.method === 'GET');
        // The customer login endpoint (/token) must stay unauthenticated: it is
        // forwarded verbatim as the real "Authorization" header to WordPress by
        // the proxy, so sending the store's Consumer Key/Secret as Basic Auth
        // here can make WordPress (or a security plugin) reject the request
        // before it even reaches the JWT login route, breaking customer login.
        const isPublicJwtLoginEndpoint = isJwtAuthRoute && endpoint.replace(/^\/+/, '').split(/[/?]/)[0] === 'token';

        if (isWordPressMediaRoute && config.wpUsername && config.wpAppPassword) {
          headers['X-WC-Authorization'] = `Basic ${btoa(`${config.wpUsername}:${config.wpAppPassword}`)}`;
        } else if (isJwtAuthRoute && explicitAuthHeader) {
          headers['X-WC-Authorization'] = explicitAuthHeader;
        } else if (!skipBasicAuth && !isPublicJwtLoginEndpoint && ck && cs) {
          headers['X-WC-Authorization'] = authHeaderValue;
        }

        fetchOptions = {
          method: options.method || 'GET',
          mode: 'cors',
          headers
        };

        if (options.body) {
          if (options.body instanceof FormData) {
            fetchOptions.body = options.body;
            if (fetchOptions.headers) delete (fetchOptions.headers as any)['Content-Type'];
          } else if (options.body instanceof File || options.body instanceof Blob) {
            fetchOptions.body = options.body;
            // Do not override Content-Type here; it should be set in options.headers
          } else {
            fetchOptions.body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
            if (fetchOptions.headers) (fetchOptions.headers as any)['Content-Type'] = 'application/json';
          }
        }

      } catch (validationErr) {
        activeRequests--;
        processQueue();
        reject(validationErr);
        return;
      }

      // Retry logic for transient server errors
      let retries = 0;
      const maxRetries = 2; // Increased to 2 for better stability
      
      const executeFetch = async (): Promise<any> => {
        try {
          const response = await fetch(proxyUrl, fetchOptions);
          const responseText = await response.text();

          // Handle specific transient errors (429, 502, 503)
          const retryableStatuses = [429, 502, 503, 504];
          const isFunctionError = response.status === 500 && responseText.includes('Function failed');
          
          if (retryableStatuses.includes(response.status) || isFunctionError) {
            if (retries < maxRetries) {
              retries++;
              const delay = 3000 * Math.pow(2, retries - 1); // Exponential backoff: 3s, 6s
              if (!isBackground) {
                toast.info(`Connection unstable (Error ${response.status}). Retrying...`, { id: 'woo-retry' });
              }
              console.warn(`[WooRequest] Transient error ${response.status}. Retrying in ${delay}ms... (Attempt ${retries}/${maxRetries})`);
              await new Promise(resolve => setTimeout(resolve, delay));
              return await executeFetch();
            }
          }

          const isHtml = responseText.includes('<!DOCTYPE') || 
                         responseText.includes('<html') || 
                         responseText.includes('<body');
          
          if (isHtml) {
            if (responseText.includes('wp-login.php') || responseText.includes('name="loginform"')) {
              throw new Error("AUTH_ERROR: O seu site WordPress redirecionou o pedido para uma página de login. Altere os Links Permanentes para 'Nome do artigo'.");
            }
            if (response.status >= 500) {
              throw new Error(`SERVER_ERROR: O servidor do App encontrou um problema técnico (Erro ${response.status}). Tente novamente em alguns segundos.`);
            }
            throw new Error("INVALID_RESPONSE: O servidor retornou uma página web em vez de JSON.");
          }

          if (!response.ok) {
            if (response.status === 401 && responseText.includes('rest_cannot_access')) {
              throw new Error("AUTH_ERROR: O seu site WordPress bloqueou o acesso à API. Verifique se os Links Permanentes estão em 'Nome do artigo' e se não tem plugins de segurança a bloquear pedidos REST.");
            }
            if (response.status === 503) {
              throw new Error("SERVICE_UNAVAILABLE: O servidor da loja está temporariamente indisponível ou a bloquear o pedido (Erro 503). Verifique se o seu site tem alguma firewall ativa.");
            }
            if (responseText.trim().startsWith('{')) {
              const json = JSON.parse(responseText);
              throw new Error(json.message || `Server Error (${response.status})`);
            }
            throw new Error(`Server Error (${response.status})`);
          }

          try {
            const parsed = JSON.parse(responseText);
            const total = response.headers.get('x-wp-total');
            const totalPages = response.headers.get('x-wp-totalpages');
            if (Array.isArray(parsed) && (total || totalPages)) {
              (parsed as any).total = total ? parseInt(total) : parsed.length;
              (parsed as any).totalPages = totalPages ? parseInt(totalPages) : 1;
            }
            return parsed;
          } catch (e) {
            return responseText;
          }
        } catch (error: any) {
          // Explicitly handle "Load failed" / Network errors
          const isNetworkError = error instanceof TypeError && (error.message.includes('failed to fetch') || error.message.includes('Load failed'));
          
          if (isNetworkError && retries < maxRetries) {
            retries++;
            const delay = 2000 * retries;
            console.warn(`[WooRequest] Network error detected. Retrying in ${delay}ms... (Attempt ${retries}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, delay));
            return executeFetch();
          }

          if (error.name === 'AbortError') throw new Error("TIMEOUT: O pedido demorou demasiado tempo a responder.");
          throw error;
        }
      };

      try {
        const result = await executeFetch();
        if (endpoint.includes('orders')) {
          console.log("[wooRequest] orders fetch result:", result);
          console.log("[wooRequest] is array?", Array.isArray(result));
        }
        resolve(result);
      } catch (fetchErr) {
        reject(fetchErr);
      }
    };

    requestQueue.push(execute);
    processQueue();
  });
}

export async function loadConfigFromServer(): Promise<WooCommerceConfig | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const headers = { 'apikey': publicAnonKey, 'Authorization': `Bearer ${session?.access_token || publicAnonKey}` };
    const response = await fetch(getServerUrl('kv/settings'), { headers });
    if (!response.ok) return null;
    const data = await response.json();
    if (data.success && data.data) return data.data;
  } catch (e) {}
  return null;
}

export async function saveConfigToServer(config: WooCommerceConfig) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const headers = { 'Content-Type': 'application/json', 'apikey': publicAnonKey, 'Authorization': `Bearer ${session?.access_token || publicAnonKey}` };
    await fetch(getServerUrl('kv/settings'), { method: 'POST', headers, body: JSON.stringify({ data: config }) });
  } catch (e) {}
}

export async function saveKVToServer(key: string, data: any) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const headers = { 'Content-Type': 'application/json', 'apikey': publicAnonKey, 'Authorization': `Bearer ${session?.access_token || publicAnonKey}` };
    await fetch(getServerUrl(`kv/${key}`), { method: 'POST', headers, body: JSON.stringify({ data }) });
  } catch (e) {}
}

export async function loadKVFromServer(key: string): Promise<any> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const headers = { 'apikey': publicAnonKey, 'Authorization': `Bearer ${session?.access_token || publicAnonKey}` };
    const response = await fetch(getServerUrl(`kv/${key}`), { headers });
    if (!response.ok) return null;
    const json = await response.json();
    return json.success ? json.data : null;
  } catch (e) { return null; }
}

export const clientAuth = {
  login: async (config: WooCommerceConfig, storeId: string, username: string, password: string, remember = false) => {
    const response = await wooRequest(
      config,
      '/token',
      {
        method: 'POST',
        body: { username, password, remember },
      },
      'swiftwoo/v1',
    );

    const token = response?.token || response?.jwt || response?.access_token;
    if (!token) {
      throw new Error(response?.message || 'Credenciais inválidas.');
    }

    // LojaPilot Connect 3.x returns a nested `user` object; the legacy bridge
    // returned flat fields. Accept both.
    const u = response?.user || {};
    const userPayload = {
      user_email: response.user_email || u.email || username,
      user_nicename: response.user_nicename || u.username || '',
      user_display_name: response.user_display_name || u.display_name || response.user_nicename || username,
      avatar_url: u.avatar_url || null,
    };

    localStorage.setItem(`client_token_${storeId}`, token);
    localStorage.setItem(`client_user_${storeId}`, JSON.stringify(userPayload));

    return {
      token,
      customer_id: response.customer_id ?? u.id ?? null,
      user_email: userPayload.user_email,
      user_display_name: userPayload.user_display_name,
      roles: u.roles || response.roles || [],
      avatar_url: u.avatar_url || null,
      user: u,
    };
  },

  // Self-service customer registration — native stores only (the Woo path
  // would need a WP-side register endpoint, which the bridge doesn't expose).
  register: async (config: WooCommerceConfig, storeId: string, payload: { email: string; password: string; first_name?: string; last_name?: string }) => {
    if (config?.connectionType !== 'native') {
      throw new Error('Registo de clientes só está disponível em lojas LojaPilot.');
    }
    const response = await wooRequest(
      config,
      '/register',
      { method: 'POST', body: payload },
      'swiftwoo/v1',
    );
    const token = response?.token;
    if (!token) throw new Error(response?.message || 'Não foi possível criar a conta.');
    const userPayload = {
      user_email: response.user_email || payload.email,
      user_nicename: response.user_nicename || '',
      user_display_name: response.user_display_name || payload.email,
    };
    localStorage.setItem(`client_token_${storeId}`, token);
    localStorage.setItem(`client_user_${storeId}`, JSON.stringify(userPayload));
    if (response.customer_id) localStorage.setItem(`client_id_${storeId}`, String(response.customer_id));
    localStorage.setItem(`client_email_${storeId}`, userPayload.user_email);
    localStorage.setItem(`client_display_name_${storeId}`, userPayload.user_display_name);
    localStorage.setItem(`client_store_url_${storeId}`, config.url || '');
    return { token, customer_id: response.customer_id, user_email: userPayload.user_email, user_display_name: userPayload.user_display_name };
  },

  logout: (storeId: string) => {
    localStorage.removeItem(`client_token_${storeId}`);
    localStorage.removeItem(`client_user_${storeId}`);
  },

  getToken: (storeId: string): string | null => {
    return localStorage.getItem(`client_token_${storeId}`);
  },

  getUser: (storeId: string): any | null => {
    const raw = localStorage.getItem(`client_user_${storeId}`);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  },

  validateToken: async (config: WooCommerceConfig, storeId: string): Promise<boolean> => {
    const token = clientAuth.getToken(storeId);
    if (!token) return false;

    try {
      // LojaPilot Connect registers /token/validate as GET only — a POST
      // returns rest_no_route (404) and the session is wrongly invalidated.
      const response = await wooRequest(
        config,
        '/token/validate',
        {
          method: 'GET',
          headers: { 'X-WC-Authorization': "Bearer " + token },
        },
        'swiftwoo/v1',
      );
      return response !== false && response !== null && response !== undefined && response?.valid !== false;
    } catch {
      return false;
    }
  },

  getMe: async (config: WooCommerceConfig, storeId: string) => {
    const token = clientAuth.getToken(storeId);
    if (!token) return null;

    try {
      const response = await wooRequest(
        config,
        '/me',
        {
          method: 'GET',
          headers: { 'X-WC-Authorization': "Bearer " + token },
        },
        'swiftwoo/v1',
      );

      if (response && typeof response === 'object') {
        // Normalize to the client_user shape the app stores — the plugin's
        // /me returns {id,email,display_name,avatar_url,roles} while legacy
        // callers read user_email/user_display_name.
        const normalized = {
          ...response,
          user_email: response.user_email || response.email || '',
          user_display_name: response.user_display_name || response.display_name || '',
          avatar_url: response.avatar_url || null,
        };
        localStorage.setItem(`client_user_${storeId}`, JSON.stringify(normalized));
        return normalized;
      }

      return response;
    } catch {
      return null;
    }
  },
};

export const WooCommerceAPI = {
  wooRequest,
  testConnection: async (config: WooCommerceConfig) => {
    return await wooRequest(config, '/products?per_page=1&_fields=id,name');
  },
  getProductsCount: async (config: WooCommerceConfig) => {
    const response = await wooRequest(config, '/products?per_page=1&_fields=id');
    if (response && typeof response === 'object' && 'total' in response) return response.total;
    if (Array.isArray(response)) return response.length;
    return 0;
  },
  getProducts: async (config: WooCommerceConfig, params: Record<string, any> = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => { if (val !== undefined && val !== null) searchParams.append(key, String(val)); });
    
    // Ensure we don't accidentally limit if not intended
    if (!searchParams.has('per_page')) searchParams.append('per_page', '100');
    if (!searchParams.has('status')) searchParams.append('status', 'any');
    if (!searchParams.has('_embed')) searchParams.append('_embed', '1');
    
    return await wooRequest(config, `/products?${searchParams.toString()}`);
  },
  getProductVariations: async (config: WooCommerceConfig, id: number) => {
    return await wooRequest(config, `/products/${id}/variations?per_page=100`);
  },
  getAllProducts: async (config: WooCommerceConfig) => {
    let all: any[] = [];
    let page = 1;
    let hasMore = true;
    
    while (hasMore && page <= 50) { // Safety cap at 5000 products
      const res = await WooCommerceAPI.getProducts(config, { page, per_page: 100 });
      if (Array.isArray(res) && res.length > 0) {
        all = [...all, ...res];
        const totalPages = (res as any).totalPages || 1;
        if (page >= totalPages && res.length < 100) hasMore = false;
        else if (res.length < 100) hasMore = false;
        page++;
      } else {
        hasMore = false;
      }
    }
    return all;
  },
  getAllMedia: async (config: WooCommerceConfig) => {
    let all: any[] = [];
    let page = 1;
    let hasMore = true;
    
    while (hasMore && page <= 50) { // Safety cap at 5000 images
      const res = await WooCommerceAPI.getWPFiles(config, { page, per_page: 100 });
      if (Array.isArray(res) && res.length > 0) {
        all = [...all, ...res];
        const totalPages = (res as any).totalPages || 1;
        if (page >= totalPages && res.length < 100) hasMore = false;
        else if (res.length < 100) hasMore = false;
        page++;
      } else {
        hasMore = false;
      }
    }
    return all;
  },
  updateProduct: async (config: WooCommerceConfig, id: number, data: any) => {
    return await wooRequest(config, `/products/${id}`, { method: 'PUT', body: data });
  },
  createProduct: async (config: WooCommerceConfig, data: any) => {
    return await wooRequest(config, '/products', { method: 'POST', body: data });
  },
  getVariations: async (config: WooCommerceConfig, productId: number) => {
    return await wooRequest(config, `/products/${productId}/variations?per_page=100`);
  },
  updateVariation: async (config: WooCommerceConfig, productId: number, variationId: number, data: any) => {
    return await wooRequest(config, `/products/${productId}/variations/${variationId}`, { method: 'PUT', body: data });
  },
  deleteVariation: async (config: WooCommerceConfig, productId: number, variationId: number) => {
    return await wooRequest(config, `/products/${productId}/variations/${variationId}?force=true`, { method: 'DELETE' });
  },
  createVariation: async (config: WooCommerceConfig, productId: number, data: any) => {
    return await wooRequest(config, `/products/${productId}/variations`, { method: 'POST', body: data });
  },
  batchVariations: async (config: WooCommerceConfig, productId: number, data: any) => {
    return await wooRequest(config, `/products/${productId}/variations/batch`, { method: 'POST', body: data });
  },
  getCategories: async (config: WooCommerceConfig) => {
    return await wooRequest(config, '/products/categories?per_page=100');
  },
  getBrands: async (config: WooCommerceConfig) => {
    const discoveryList = [
      { namespace: 'wc/v3', endpoint: 'products/brands' },
      { namespace: 'wc/v3', endpoint: 'brands' },
      { namespace: 'wp/v2', endpoint: 'pwb-brand' },
      { namespace: 'wp/v2', endpoint: 'product_brand' },
      { namespace: 'wc-brand/v1', endpoint: 'brands' },
      { namespace: 'yith-common/v1', endpoint: 'brands' },
      { namespace: 'wc-pwb/v1', endpoint: 'brands' },
      { namespace: 'wp/v2', endpoint: 'brand' },
      { namespace: 'wp/v2', endpoint: 'product_brands' },
      { namespace: 'wc/v2', endpoint: 'products/brands' }
    ];
    
    for (const item of discoveryList) {
      try {
        const data = await wooRequest(config, `${item.endpoint}?per_page=100`, { method: 'GET' }, item.namespace);
        if (Array.isArray(data) && data.length > 0) return data;
        if (data && typeof data === 'object' && !Array.isArray(data)) {
           const potentialArray = (data as any).brands || (data as any).data || (data as any).items || [];
           if (Array.isArray(potentialArray) && potentialArray.length > 0) return potentialArray;
        }
      } catch (e) {}
    }
    
    try {
      const attributes = await wooRequest(config, 'products/attributes', { method: 'GET' }, 'wc/v3');
      if (Array.isArray(attributes)) {
        const brandAttr = attributes.find(a => 
          a.name.toLowerCase().includes('brand') || 
          a.name.toLowerCase().includes('marca') ||
          a.name.toLowerCase().includes('fabricante')
        );
        if (brandAttr) {
          const terms = await wooRequest(config, `products/attributes/${brandAttr.id}/terms?per_page=100`, { method: 'GET' }, 'wc/v3');
          if (Array.isArray(terms) && terms.length > 0) return terms;
        }
      }
    } catch (e) {}
    return [];
  },
  createBrand: async (config: WooCommerceConfig, data: any) => {
    try { 
      const res = await wooRequest(config, '/products/brands', { method: 'POST', body: data }); 
      if (res && res.id) return res;
    } catch (e) {}
    try { return await wooRequest(config, '/pwb-brand', { method: 'POST', body: data }, 'wp/v2'); }
    catch (e) { return await wooRequest(config, '/brands', { method: 'POST', body: data }); }
  },
  updateBrand: async (config: WooCommerceConfig, id: number, data: any) => {
    try { 
      const res = await wooRequest(config, `/products/brands/${id}`, { method: 'PUT', body: data }); 
      if (res && res.id) return res;
    } catch (e) {}
    try { return await wooRequest(config, `/pwb-brand/${id}`, { method: 'PUT', body: data }, 'wp/v2'); }
    catch (e) { return await wooRequest(config, `/brands/${id}`, { method: 'PUT', body: data }); }
  },
  deleteBrand: async (config: WooCommerceConfig, id: number) => {
    try { 
      const res = await wooRequest(config, `/products/brands/${id}?force=true`, { method: 'DELETE' }); 
      if (res) return res;
    } catch (e) {}
    try { return await wooRequest(config, `/pwb-brand/${id}?force=true`, { method: 'DELETE' }, 'wp/v2'); }
    catch (e) { return await wooRequest(config, `/brands/${id}?force=true`, { method: 'DELETE' }); }
  },
  getMedia: async (config: WooCommerceConfig, params: Record<string, any> = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null) searchParams.append(key, String(val));
    });
    if (!searchParams.has('per_page')) searchParams.append('per_page', '100');
    if (!searchParams.has('media_type')) searchParams.append('media_type', 'image');

    const response = await wooRequest(config, `/media?${searchParams.toString()}`, { method: 'GET' }, 'wp/v2');
    const items = Array.isArray(response) ? response : [];
    return items.map((item: any) => ({
      ...item,
      source_url: cleanImageUrl(item?.source_url),
      media_details: item?.media_details || { width: 0, height: 0 },
    }));
  },
  getWPFiles: async (config: WooCommerceConfig, params: Record<string, any> = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => { if (val !== undefined && val !== null) searchParams.append(key, String(val)); });
    if (!searchParams.has('per_page')) searchParams.append('per_page', '100');
    if (!searchParams.has('media_type')) searchParams.append('media_type', 'image');

    // Enforce sorting
    searchParams.append('orderby', 'date');
    searchParams.append('order', 'desc');
    
    // Cache busting
    searchParams.append('_t', Date.now().toString());

    return await wooRequest(config, `/media?${searchParams.toString()}`, { method: 'GET', cache: 'no-store' }, 'wp/v2');
  },
  uploadMedia: async (config: WooCommerceConfig, file: File, storeId?: string) => {
    const formData = new FormData();
    formData.append('file', file, file.name);

    try {
      const response = await wooRequest(
        config,
        '/media',
        {
          method: 'POST',
          body: formData,
        },
        'wp/v2',
      );

      if (response && typeof response === 'object' && 'error_message' in response && response.error_message) {
        throw new Error(String(response.error_message));
      }
      if (response && typeof response === 'object' && 'message' in response && typeof response.message === 'string' && !('id' in response)) {
        throw new Error(response.message);
      }

      return response;
    } catch (error: any) {
      const details = error?.response || error?.data || {};
      const wpMessage = details?.error_message || error?.message || 'Upload failed';
      throw new Error(wpMessage);
    }
  },
  deleteMedia: async (config: WooCommerceConfig, id: number) => {
    return await wooRequest(config, `/media/${id}?force=true`, { method: 'DELETE' }, 'wp/v2');
  },

  getSiteInfo: async (config: WooCommerceConfig) => {
    return await wooRequest(config, '/', {}, 'wp/v2');
  },
  getAttributes: async (config: WooCommerceConfig) => {
    return await wooRequest(config, '/products/attributes');
  },
  getAttributeTerms: async (config: WooCommerceConfig, attributeId: number) => {
    return await wooRequest(config, `/products/attributes/${attributeId}/terms?per_page=100`);
  },
  updateAttributeTerm: async (config: WooCommerceConfig, attributeId: number, termId: number, data: any) => {
    return await wooRequest(config, `/products/attributes/${attributeId}/terms/${termId}`, { method: 'PUT', body: data });
  },
  getStoreSettings: async (config: WooCommerceConfig) => {
    try { return await wooRequest(config, '/settings/general'); } catch (e) { return []; }
  },
  getOrders: async (config: WooCommerceConfig, params: Record<string, any> = {}) => {
    const searchParams = new URLSearchParams();
    let isNotification = false;
    Object.entries(params).forEach(([key, val]) => { 
      if (key === 'isNotification') {
        isNotification = !!val;
      } else if (val !== undefined && val !== null) {
        searchParams.append(key, String(val)); 
      }
    });
    
    // Explicitly enforce status: any to ensure completed orders are included, 
    // overriding any strict server defaults, ONLY if no status is already provided
    if (!searchParams.has('status')) {
      searchParams.append('status', 'any');
    }
    
    return await wooRequest(config, `/orders?${searchParams.toString()}`, { isNotification } as any);
  },
  updateOrder: async (config: WooCommerceConfig, id: number, data: any) => {
    return await wooRequest(config, `/orders/${id}`, { method: 'PUT', body: data });
  },
  createOrder: async (config: WooCommerceConfig, data: any) => {
    return await wooRequest(config, '/orders', { method: 'POST', body: data });
  },
  getOrderNotes: async (config: WooCommerceConfig, orderId: number) => {
    return await wooRequest(config, `/orders/${orderId}/notes`);
  },
  createOrderNote: async (config: WooCommerceConfig, orderId: number, note: string, customerNote: boolean = false) => {
    return await wooRequest(config, `/orders/${orderId}/notes`, { method: 'POST', body: { note, customer_note: customerNote } });
  },
  getSalesReport: async (config: WooCommerceConfig, params: Record<string, any> = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => { if (val !== undefined && val !== null) searchParams.append(key, String(val)); });
    return await wooRequest(config, `/reports/sales?${searchParams.toString()}`);
  },
  getProductTotalsReport: async (config: WooCommerceConfig) => {
    return await wooRequest(config, '/reports/products/totals');
  },
  getTopSellersReport: async (config: WooCommerceConfig, period: string = 'last_7_days') => {
    return await wooRequest(config, `/reports/top_sellers?period=${period}`);
  },
  getCustomers: async (config: WooCommerceConfig, params: Record<string, any> = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => { if (val !== undefined && val !== null) searchParams.append(key, String(val)); });
    return await wooRequest(config, `/customers?${searchParams.toString()}`);
  },
  getCustomer: async (config: WooCommerceConfig, id: number) => {
    return await wooRequest(config, `/customers/${id}`);
  },
  createCustomer: async (config: WooCommerceConfig, data: any) => {
    return await wooRequest(config, '/customers', { method: 'POST', body: data });
  },
  updateCustomer: async (config: WooCommerceConfig, id: number, data: any) => {
    return await wooRequest(config, `/customers/${id}`, { method: 'PUT', body: data });
  },
  getPaymentGateways: async (config: WooCommerceConfig) => {
    return await wooRequest(config, '/payment_gateways');
  },
  // --- Store API (v1) ---
  storeApiCartAddItem: async (storeId: string, productId: number, quantity: number) => {
    return await storeApiRequest(storeId, '/cart/add-item', { method: 'POST', body: { id: productId, quantity } });
  },
  storeApiCartUpdateCustomer: async (storeId: string, billing: any, shipping: any) => {
    return await storeApiRequest(storeId, '/cart/update-customer', { method: 'POST', body: { billing_address: billing, shipping_address: shipping } });
  },
  storeApiCartSelectShippingRate: async (storeId: string, rateId: string, packageId: string | number = 0) => {
    return await storeApiRequest(storeId, '/cart/select-shipping-rate', { method: 'POST', body: { package_id: packageId, rate_id: rateId } });
  },
  storeApiGetCheckout: async (storeId: string) => {
    return await storeApiRequest(storeId, '/checkout');
  },
  storeApiSubmitCheckout: async (storeId: string, data: any) => {
    return await storeApiRequest(storeId, '/checkout', { method: 'POST', body: data });
  }
};

export const STORE_KEYS = { CONFIG: 'wc_api_config', CURRENCY: 'wc_currency' };

export function saveConfig(config: WooCommerceConfig) {
  if (config?.url) {
    localStorage.setItem('wc_active_store_url', config.url);
  }
  localStorage.setItem(STORE_KEYS.CONFIG, JSON.stringify(config));
  saveConfigToServer(config);
}

export function loadConfig(): WooCommerceConfig | null {
  const saved = localStorage.getItem(STORE_KEYS.CONFIG);
  try {
    if (saved) return JSON.parse(saved);
    const fallbackUrl = localStorage.getItem('wc_active_store_url');
    if (fallbackUrl) {
      return { url: fallbackUrl, consumerKey: '', consumerSecret: '', version: 'wc/v3' } as WooCommerceConfig;
    }
    return null;
  } catch (e) { return null; }
}

/**
 * WooCommerce Store API helper (v1)
 * Used specifically for native Checkout flow via client JWT.
 */
export async function storeApiRequest(
  storeId: string, 
  endpoint: string, 
  options: Omit<RequestInit, 'body'> & { body?: any } = {}
): Promise<any> {
  const clientStoreUrl = localStorage.getItem(`client_store_url_${storeId}`);
  const config = loadConfig();
  
  let baseUrl = '';
  if (clientStoreUrl) {
    baseUrl = clientStoreUrl.endsWith('/') ? clientStoreUrl.slice(0, -1) : clientStoreUrl;
  } else if (config && config.url) {
    baseUrl = config.url.endsWith('/') ? config.url.slice(0, -1) : config.url;
  } else {
    throw new Error("Store URL not configured");
  }
  const targetUrl = `${baseUrl}/wp-json/wc/store/v1${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  
  // Get Client JWT — optional: the WC Store API serves guest carts and
  // guest checkout natively, so unauthenticated storefront visitors call
  // these endpoints without a token. The plugin's client-JWT header is
  // only attached when a session exists.
  const jwt = localStorage.getItem(`client_token_${storeId}`);

  const { data: { session } } = await supabase.auth.getSession();
  const proxyUrl = getServerUrl('proxy');

  const appDomain = localStorage.getItem('wc_app_domain') || window.location.hostname;
  const channel = (window.location.pathname || '').startsWith('/loja')
    ? `${appDomain}/loja`
    : appDomain;

  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) || {}),
    'X-Proxy-Target': targetUrl,
    'apikey': publicAnonKey,
    'Authorization': `Bearer ${session?.access_token || publicAnonKey}`,
    'X-SwiftWoo-Domain': appDomain,
    'X-SwiftWoo-Channel': channel,
    ...(jwt ? { 'X-WC-Authorization': `Bearer ${jwt}` } : {})
  };

  const clientId = localStorage.getItem(`client_id_${storeId}`) || 'guest';
  const nonceKey = `swiftwoo:store-api-nonce:${storeId}:${clientId}`;
  let savedNonce = localStorage.getItem(nonceKey) || '';

  if (savedNonce) {
    headers['Nonce'] = savedNonce;
    headers['X-WC-Store-API-Nonce'] = savedNonce;
  }

  // Guest cart session is keyed by Cart-Token — without resending it every
  // request would hit a fresh empty cart.
  const cartTokenKey = `swiftwoo:store-api-cart-token:${storeId}:${clientId}`;
  const savedCartToken = localStorage.getItem(cartTokenKey) || '';
  if (savedCartToken) headers['Cart-Token'] = savedCartToken;

  const fetchOptions: RequestInit = {
    method: options.method || 'GET',
    mode: 'cors',
    headers
  };

  if (options.body) {
    fetchOptions.body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
    (fetchOptions.headers as any)['Content-Type'] = 'application/json';
  }
  
  const cacheBuster = proxyUrl.includes('?') ? `&_cb=${Date.now()}` : `?_cb=${Date.now()}`;
  const response = await fetch(`${proxyUrl}${cacheBuster}`, fetchOptions);
  
  // Extract and save the nonce if it was returned
  const newNonce = response.headers.get('nonce') || response.headers.get('x-wc-store-api-nonce');
  if (newNonce) {
    localStorage.setItem(nonceKey, newNonce);
  }

  const newCartToken = response.headers.get('cart-token');
  if (newCartToken) {
    localStorage.setItem(cartTokenKey, newCartToken);
  }

  const responseText = await response.text();

  if (!response.ok) {
    let errorMsg = `Store API Error (${response.status})`;
    try {
      const data = JSON.parse(responseText);
      if (data.message) errorMsg = data.message.replace(/<[^>]*>?/gm, '');
    } catch(e) {}
    throw new Error(errorMsg);
  }
  
  return responseText ? JSON.parse(responseText) : { success: true };
}
